import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    Container,
    Button,
    Row, Col, Label, Input
} from 'reactstrap';
import ReactTable from 'react-table';
import { Link } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.css';
import "react-table/react-table.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faCircle } from '@fortawesome/free-solid-svg-icons';
import { faStar as faStarOutline, faCircle as faCircleOutline } from '@fortawesome/free-regular-svg-icons';
import CollapseCard from '../../components/CollapseCard/CollapseCard';
import PermissionWrapper from '../../auth/PermissionWrapper';
import CompanyContext from '../../auth/CompanyContext';
import ContactsDetail2 from '../../components/Contacts2/ContactsDetail2';
import { Prompt } from 'react-router-dom';

const Contacts2 = (props) => {
    const companyCtx = useContext(CompanyContext);

    const { history } = props;

    const path = useMemo(() => String(props.location?.pathname || '').split('/')[2] || '', [props.location?.pathname]);

    const abortRef = useRef(null);
    const mountedRef = useRef(true);
    const pathRef = useRef(String(path || '').toLowerCase());

    const [isLoading, setIsLoading] = useState(false);
    const [contacts, setContacts] = useState([]);
    const [hasFullContacts, setHasFullContacts] = useState(false);
    const [contactLookupStatus, setContactLookupStatus] = useState('idle');
    const [selectedRow, setSelectedRow] = useState(null);
    const [modalOpen, setModalOpen] = useState(Boolean(props?.location?.state?.modalOpen || path));

    const [editCategoryCommentsArray, setEditCategoryCommentsArray] = useState([]);
    const [addCategoryCommentsArray, setAddCategoryCommentsArray] = useState([]);
    const [addCategorySubCommentsArray, setAddCategorySubCommentsArray] = useState([]);

    const [notesCategories, setNotesCategories] = useState([]);

    const [editID, setEditID] = useState(path || 0);
    const [companies, setCompanies] = useState([]);

    const [showInactive, setShowInactive] = useState(false);
    const [showMissingCompanies, setShowMissingCompanies] = useState(false);

    const [scrollYBeforeModal, setScrollYBeforeModal] = useState(0);

    const [reactTablePage, setReactTablePage] = useState(0);
    const [reactTablePageSize, setReactTablePageSize] = useState(50);
    const [reactTableSorted, setReactTableSorted] = useState([]);
    const [reactTableFiltered, setReactTableFiltered] = useState([]);

    const [unsavedChanges, setUnsavedChanges] = useState(false);
    const [pendingPathNavigation, setPendingPathNavigation] = useState(null);

    useEffect(() => {
        return () => {
            mountedRef.current = false;
            if (abortRef.current) abortRef.current.abort();
        };
    }, []);

    useEffect(() => {
        pathRef.current = String(path || '').toLowerCase();
    }, [path]);

    useEffect(() => {
        setContactLookupStatus('idle');
    }, [path]);

    useEffect(() => {
        if (!pendingPathNavigation) return;
        if (unsavedChanges) return;

        if (props.location?.pathname !== pendingPathNavigation) {
            history.push(pendingPathNavigation);
        }
        setPendingPathNavigation(null);
    }, [pendingPathNavigation, unsavedChanges, history, props.location?.pathname]);

    const noBlanksFilterMethod = useCallback((filter, row) => {
        const filterValue = String(filter.value || '').toLowerCase().trim();
        const cellValue = row[filter.id];

        if (!filterValue) return true;
        if (!cellValue || cellValue.toString().trim() === '') return false;

        if (Array.isArray(cellValue)) {
            return cellValue.some(contact => {
                const name = contact?.toLowerCase().trim() || '';
                return name.includes(filterValue);
            });
        }

        return cellValue.toString().toLowerCase().includes(filterValue);
    }, []);

    const sortMethod = useCallback((a, b) => {
        const valA = a ? a.toString().toLowerCase().trim() : "";
        const valB = b ? b.toString().toLowerCase().trim() : "";

        if (valA === "" && valB !== "") return 1;
        if (valB === "" && valA !== "") return -1;

        return valA.localeCompare(valB);
    }, []);

    const parseNdjsonStream = useCallback(async ({ response, onRow, signal }) => {
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let leftover = "";

        try {
            while (true) {
                if (signal?.aborted) break;

                let result;
                try {
                    result = await reader.read();
                } catch (e) {
                    if (e?.name === "AbortError") break;
                    throw e;
                }

                const { value, done } = result;
                if (done) break;

                const chunk = decoder.decode(value, { stream: true });
                const text = leftover + chunk;
                const lines = text.split("\n");
                leftover = lines.pop() || "";

                for (const line of lines) {
                    const trimmed = line.trim();
                    if (!trimmed) continue;
                    try {
                        onRow?.(JSON.parse(trimmed));
                    } catch {
                        // ignore bad line
                    }
                }
            }
        } finally {
            // If we stop early, explicitly cancel the reader to release resources
            try { await reader.cancel(); } catch { }
        }

        if (!signal?.aborted) {
            const tail = leftover.trim();
            if (tail) {
                try { onRow?.(JSON.parse(tail)); } catch { }
            }
        }
    }, []);

    const toggleModal = useCallback(() => {
        setModalOpen(prevOpen => {
            const closing = prevOpen;
            if (closing) {
                // closing: clear companies after close
                setCompanies([]);
                // restore scroll after React paints
                setTimeout(() => window.scrollTo({ top: scrollYBeforeModal }), 0);
            }
            return !prevOpen;
        });
    }, [scrollYBeforeModal]);

    const favoriteCallback = useCallback((data) => {
        setContacts(prev =>
            prev.map(result => ({
                ...result,
                favorites: ("contact" in data && data.contact.some(item => item.Favorite === result.linkedID)) ? 1 : ''
            }))
        );

        setSelectedRow(prev => {
            if (!prev) return prev;
            const fav = ("contact" in data && data.contact.some(item => item.Favorite === prev.linkedID)) ? 1 : '';
            return { ...prev, favorites: fav };
        });
    }, []);

    const getNotesCategories = useCallback(() => {
        fetch('/api/notesCategories/', {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.clientToken,
                'X-Database': companyCtx.company
            }
        })
            .then(r => r.json())
            .then(results => {
                if (!mountedRef.current) return;
                const contactNode = results.data.filter(item => item.type === 'Contact')[0];
                const salesNode = results.data.filter(item => item.type === 'Sales Activity')[0];

                setNotesCategories(contactNode?.children || []);
                setAddCategoryCommentsArray(contactNode?.children || []);
                setAddCategorySubCommentsArray(salesNode?.children || []);
            });
    }, [props.clientToken, companyCtx.company]);

    const getContacts = useCallback(async () => {
        if (abortRef.current) abortRef.current.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        setIsLoading(true);

        const headers = {
            Accept: 'application/x-ndjson',
            Authorization: 'Bearer ' + props.clientToken,
            'X-Database': companyCtx.company,
        };

        try {
            const url = `/api/contacts/${props.user}`;
            const resp = await fetch(url, {
                method: 'GET',
                headers,
                signal: controller.signal,
            });

            if (!mountedRef.current || controller.signal.aborted) return [];

            if (!resp.ok || !resp.body) {
                setIsLoading(false);
                return [];
            }

            const next = [];
            let raf = null;

            const flush = () => {
                if (!mountedRef.current || controller.signal.aborted) return;
                setContacts(next.slice());
                raf = null;
            };

            await parseNdjsonStream({
                response: resp,
                signal: controller.signal,
                onRow: (row) => {
                    next.push(row);
                    if (!raf) raf = requestAnimationFrame(flush);
                },
            });

            if (!mountedRef.current || controller.signal.aborted) return next;

            setContacts(next);
            setHasFullContacts(true);
            setIsLoading(false);
            return next;
        } catch (e) {
            // THIS is the key: abort is not an error case
            if (e?.name === 'AbortError') {
                if (mountedRef.current) setIsLoading(false);
                return [];
            }
            setIsLoading(false);
            throw e;
        }
    }, [companyCtx.company, parseNdjsonStream, props.clientToken, props.user]);

    const getContactByGUID = useCallback(async (contactGUID) => {
        const guid = String(contactGUID || '').trim();
        if (!guid) return [];

        if (abortRef.current) abortRef.current.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        setIsLoading(true);

        const headers = {
            Accept: 'application/x-ndjson',
            Authorization: 'Bearer ' + props.clientToken,
            'X-Database': companyCtx.company,
        };

        try {
            const url = `/api/contacts/${props.user}/0/${guid}`;
            const resp = await fetch(url, {
                method: 'GET',
                headers,
                signal: controller.signal,
            });

            if (!mountedRef.current || controller.signal.aborted) return [];

            if (!resp.ok || !resp.body) {
                setIsLoading(false);
                return [];
            }

            const next = [];
            await parseNdjsonStream({
                response: resp,
                signal: controller.signal,
                onRow: (row) => {
                    next.push(row);
                },
            });

            if (!mountedRef.current || controller.signal.aborted) return next;

            setContacts(next);
            setHasFullContacts(false);
            setIsLoading(false);
            return next;
        } catch (e) {
            if (e?.name === 'AbortError') {
                if (mountedRef.current) setIsLoading(false);
                return [];
            }
            setIsLoading(false);
            throw e;
        }
    }, [companyCtx.company, parseNdjsonStream, props.clientToken, props.user]);

    const buildEditCategoryCommentsArray = useCallback((contactRow) => {
        if (!contactRow || !contactRow.children) return [];

        // clone so we never mutate notesCategories
        const categoryList = (notesCategories || []).map(item => ({ ...item }));

        categoryList.forEach(item => {
            const fieldKey = item.fieldKey || item.type;
            if (Object.prototype.hasOwnProperty.call(contactRow.children, fieldKey)) {
                item.value = contactRow.children[fieldKey];
            } else {
                item.value = contactRow.children?.[item.type] ?? "";
            }
        });

        const inactiveIdx = categoryList.findIndex(item => item.type === "Inactive");
        if (inactiveIdx > -1) categoryList[inactiveIdx].value = !contactRow.active;

        return categoryList;
    }, [notesCategories]);

    const handleEditSwap = useCallback((val) => {
        // OPEN
        if (val) {
            setEditID(val);
            setUnsavedChanges(false);
            setModalOpen(true);

            if (props.location?.pathname !== `/contacts/${val}`) {
                setPendingPathNavigation(`/contacts/${val}`);
            }
            return;
        }

        // CLOSE
        setModalOpen(false);
        setEditID(0);
        setSelectedRow(null);
        setCompanies([]);
        setEditCategoryCommentsArray([]);
        setUnsavedChanges(false);

        if (props.location?.pathname !== `/contacts`) {
            setPendingPathNavigation(`/contacts`);
        }
    }, [props.location?.pathname]);

    const handleRowClick = useCallback((row, event) => {
        if (event?.target?.tagName === "A") return;

        setScrollYBeforeModal(window.scrollY);

        setSelectedRow(row);
        setCompanies(row?.companies || []);

        // PRE-HYDRATE HERE
        const hydrated = buildEditCategoryCommentsArray(row);
        setEditCategoryCommentsArray(hydrated);

        window.scrollTo({ top: 0 });

        // open + url + editID after hydration is queued (same render batch)
        handleEditSwap(row.linkedID);
    }, [handleEditSwap, buildEditCategoryCommentsArray]);

    const handleContactDeleted = useCallback((deletedGUID) => {
        const id = String(deletedGUID || '').toLowerCase().trim();
        if (!id) return;
        setContacts(prev => prev.filter(item => String(item?.linkedID || '').toLowerCase() !== id));
    }, []);

    const handleContactPatched = useCallback((patch = {}) => {
        const id = String(patch?.linkedID || '').toLowerCase().trim();
        if (!id) return;

        setContacts(prev => prev.map(item => {
            if (String(item?.linkedID || '').toLowerCase() !== id) return item;

            const next = { ...item };
            if (patch.children && typeof patch.children === 'object') {
                next.children = patch.children;
            }
            if (typeof patch.active === 'boolean') {
                next.active = patch.active;
            }
            if (Array.isArray(patch.companies)) {
                next.companies = patch.companies;
            }
            return next;
        }));

        setSelectedRow(prev => {
            if (!prev || String(prev?.linkedID || '').toLowerCase() !== id) return prev;

            const next = { ...prev };
            if (patch.children && typeof patch.children === 'object') {
                next.children = patch.children;
            }
            if (typeof patch.active === 'boolean') {
                next.active = patch.active;
            }
            if (Array.isArray(patch.companies)) {
                next.companies = patch.companies;
            }
            return next;
        });
    }, []);

    useEffect(() => {
        if (!props.clientToken) return;
        if (!companyCtx.company) return;
        if (!props.user) return;

        getNotesCategories();
    }, [props.clientToken, props.user, companyCtx.company, getNotesCategories]);

    useEffect(() => {
        if (!props.clientToken) return;
        if (!companyCtx.company) return;
        if (!props.user) return;
        if (isLoading) return;

        const pathNormalized = String(path || '').toLowerCase();
        const hasPath = pathNormalized.length > 0;
        const hasInMemoryPathRow = hasPath && contacts.some(c => String(c.linkedID || '').toLowerCase() === pathNormalized);

        if (hasPath) {
            if (hasInMemoryPathRow) {
                if (contactLookupStatus !== 'found') setContactLookupStatus('found');
                return;
            }

            if (contactLookupStatus === 'loading' || contactLookupStatus === 'not_found') {
                return;
            }

            setContactLookupStatus('loading');
            void (async () => {
                const rows = await getContactByGUID(pathNormalized);
                if (!mountedRef.current) return;
                if (pathRef.current !== pathNormalized) return;
                setContactLookupStatus(rows.length > 0 ? 'found' : 'not_found');
            })();
            return;
        }

        if (contactLookupStatus !== 'idle') setContactLookupStatus('idle');
        if (!hasFullContacts) {
            void getContacts();
        }
    }, [props.clientToken, props.user, companyCtx.company, path, hasFullContacts, contacts, getContacts, getContactByGUID, isLoading, contactLookupStatus]);

    useEffect(() => {
        if (!path) {
            if (!modalOpen) return;
            if (!editID) return;

            setModalOpen(false);
            setEditID(0);
            setSelectedRow(null);
            setCompanies([]);
            setEditCategoryCommentsArray([]);
            return;
        }
        if (!contacts.length) return;
        if (isLoading) return;
        if (contactLookupStatus === 'not_found') {
            setSelectedRow(null);
            setCompanies([]);
            setEditCategoryCommentsArray([]);
            setEditID(path);
            setModalOpen(true);
            return;
        }

        const pathNormalized = String(path || '').toLowerCase();
        const row = contacts.find(c => String(c.linkedID || '').toLowerCase() === pathNormalized);

        if (!row) {
            if (!isLoading) {
                setContactLookupStatus('not_found');
            }
            return;
        }

        setSelectedRow(row);
        setCompanies(row?.companies || []);
        if (notesCategories.length > 0 && !unsavedChanges) {
            setEditCategoryCommentsArray(buildEditCategoryCommentsArray(row));
        }
        setEditID(path);
        setModalOpen(true);
    }, [path, contacts, notesCategories.length, buildEditCategoryCommentsArray, isLoading, modalOpen, editID, contactLookupStatus, unsavedChanges]);

    const contactColumns = useMemo(() => ([
        {
            Header: <FontAwesomeIcon icon={faStar} />,
            accessor: 'favorites',
            filterable: false,
            width: 60,
            Cell: row => (parseInt(row.original['favorites']) > 0)
                ? (
                    <FontAwesomeIcon
                        icon={faStar}
                        className="favoriteIcon"
                        onClick={(e) => {
                            e.stopPropagation();
                            props.removeFavorite('contact', row.original['linkedID'], favoriteCallback);
                        }}
                    />
                )
                : (
                    <FontAwesomeIcon
                        icon={faStarOutline}
                        className="favoriteIconDisabled"
                        onClick={(e) => {
                            e.stopPropagation();
                            props.addFavorite('contact', row.original['linkedID'], favoriteCallback);
                        }}
                    />
                )
        },
        {
            Header: 'Active',
            accessor: 'active',
            filterable: false,
            width: 60,
            Cell: row => (row.original['active'])
                ? (
                    <FontAwesomeIcon
                        icon={faCircle}
                        className="iconActive"
                    />
                )
                : (
                    <FontAwesomeIcon
                        icon={faCircleOutline}
                        className="iconInactive"
                    />
                )
        },
        {
            Header: 'Contact Name',
            accessor: d => d.children ? d.children["Contact Name"] : "",
            id: 'Contact Name',
            filterable: true,
            filterMethod: noBlanksFilterMethod,
            sortMethod,
            Cell: (p) => <span className="text-primary">{p.value}</span>,
        },
        {
            Header: 'Title',
            accessor: d => d.children ? d.children["Title"] : "",
            id: 'Title',
            filterable: true,
            filterMethod: noBlanksFilterMethod,
            sortMethod
        },
        {
            Header: 'Phone',
            accessor: d => d.children ? d.children["Phone Number"] : "",
            id: 'Phone',
            filterable: true,
            width: 150,
            filterMethod: noBlanksFilterMethod,
            sortMethod
        },
        {
            Header: 'Email',
            accessor: d => d.children ? d.children["Email Address"] : "",
            id: 'Email',
            filterable: true,
            filterMethod: noBlanksFilterMethod,
            sortMethod
        },
        {
            Header: 'Companies',
            id: 'companies',
            accessor: 'companies',
            width: 400,
            Cell: (p) => {
                if (!p.value || !Array.isArray(p.value)) return null;
                return (
                    <>
                        {p.value.map((company, index) => {
                            const primaryBusinessName = company.primaryBusinessName || 'Unknown';
                            const secID = company.secID || '';
                            return (
                                <span key={secID + '_' + index}>
                                    <Link
                                        to={{ pathname: `/demographics/${secID}`, state: {} }}
                                        style={{ marginRight: '8px' }}
                                    >
                                        {primaryBusinessName} ({secID})
                                    </Link>
                                    {index < p.value.length - 1 ? <br /> : null}
                                </span>
                            );
                        })}
                    </>
                );
            },
            filterable: true,
            filterMethod: (filter, row) => {
                if (!filter.value) return true;
                const companiesArr = row[filter.id] || [];
                const q = String(filter.value).toLowerCase();
                return companiesArr.some(company => {
                    const primaryBusinessName = company.primaryBusinessName?.toLowerCase() || '';
                    const secID = company.secID?.toLowerCase() || '';
                    return primaryBusinessName.includes(q) || secID.includes(q);
                });
            }
        },
        {
            Header: 'Created',
            accessor: 'created',
            width: 100,
            Cell: (p) => {
                const custom_date = p.value || '';
                if (custom_date.length < 10) return null;
                const custom_date_format = custom_date.substr(5, 2) + '/' + custom_date.substr(8, 2) + '/' + custom_date.substr(0, 4);
                return <>{custom_date_format}</>;
            }
        },
        {
            Header: 'Updated',
            accessor: 'timestamp',
            width: 100,
            Cell: (p) => {
                const custom_date = p.value || '';
                if (custom_date.length < 10) return null;
                const custom_date_format = custom_date.substr(5, 2) + '/' + custom_date.substr(8, 2) + '/' + custom_date.substr(0, 4);
                return <>{custom_date_format}</>;
            }
        },
    ]), [
        favoriteCallback,
        noBlanksFilterMethod,
        sortMethod,
        props.addFavorite,
        props.removeFavorite
    ]);

    const dataContacts = useMemo(() => {
        return contacts.filter(contact => {
            if (path) return true;

            if (showInactive && contact.active) return false;
            if (showMissingCompanies && contact.companies?.length > 0) return false;

            return true;
        });
    }, [contacts, showInactive, showMissingCompanies, path]);

    const contactTitle = useMemo(() => {
        if (path) return 'Contact Detail';
        if (showInactive && showMissingCompanies) return 'Inactive & Missing Companies';
        if (showInactive) return 'Inactive Contacts';
        if (showMissingCompanies) return 'Contacts With Missing Companies';
        return 'Active & Matched Contacts';
    }, [path, showInactive, showMissingCompanies]);

    const showContactNotFound = Boolean(path && contactLookupStatus === 'not_found');

    return (
        <PermissionWrapper {...props} allowedPermission="Contacts">
            <Prompt
                when={unsavedChanges}
                message="You have unsaved changes, are you sure you want to leave?"
            />

            <Container fluid className='pz-0'>
                {showContactNotFound ? (
                    <Row className="py-5">
                        <Col className="text-center">
                            <h3>Contact Not Found</h3>
                            <Button color="primary" onClick={() => handleEditSwap('')}>
                                Back to Contacts List
                            </Button>
                        </Col>
                    </Row>
                ) : modalOpen && (
                    <ContactsDetail2
                        clientToken={props.clientToken}
                        selectedRow={selectedRow}
                        setSelectedRow={setSelectedRow}
                        editID={editID}
                        setEditID={(id) => setEditID(id)}
                        companies={companies}
                        setCompanies={(c) => setCompanies(c)}
                        toggleModal={toggleModal}
                        refreshContacts={() => {
                            if (path && !hasFullContacts) return getContactByGUID(path);
                            return getContacts();
                        }}
                        contacts={contacts}
                        user={props.user}
                        editCategoryCommentsArray={editCategoryCommentsArray}
                        addCategoryCommentsArray={addCategoryCommentsArray}
                        addCategorySubCommentsArray={addCategorySubCommentsArray}
                        notesCategories={notesCategories}
                        handleEditSwap={handleEditSwap}
                        setEditCategoryCommentsArray={setEditCategoryCommentsArray}
                        setAddCategoryCommentsArray={setAddCategoryCommentsArray}
                        setModalOpen={setModalOpen}
                        userPermissions={props.userPermissions}
                        getFavorites={props.getFavorites}
                        addFavorite={props.addFavorite}
                        removeFavorite={props.removeFavorite}
                        favoriteCallback={favoriteCallback}
                        companySettings={props.companySettings}
                        idToken={props.idToken}
                        onContactDeleted={handleContactDeleted}
                        onContactPatched={handleContactPatched}
                        onUnsavedChangesChange={setUnsavedChanges}
                    />
                )}

                {<div
                    style={{
                        visibility: (modalOpen || showContactNotFound) ? 'hidden' : 'visible',
                        pointerEvents: (modalOpen || showContactNotFound) ? 'none' : 'auto',
                        height: (modalOpen || showContactNotFound) ? 0 : 'auto',
                        overflow: 'hidden',
                    }}
                >
                    <>
                        <Row>
                            <Col md={3} className='text-left pt-3 pl-5'>
                                <Label>
                                    <Input
                                        type="checkbox"
                                        checked={showInactive}
                                        name="showInactive"
                                        onChange={() => setShowInactive(v => !v)}
                                    />
                                    Show Inactive
                                </Label>
                                <Label>
                                    <Input
                                        type="checkbox"
                                        checked={showMissingCompanies}
                                        name="showMissingCompanies"
                                        onChange={() => setShowMissingCompanies(v => !v)}
                                    />
                                    Show Missing Companies
                                </Label>
                            </Col>
                            <Col md={6} className='text-center'>
                                <PermissionWrapper {...props} allowedPermission="Show Secondary Ribbon" hidden={true}>
                                    <h1>Contacts</h1>
                                </PermissionWrapper>
                            </Col>
                            <Col md={3} className="text-right pt-4 pr-4">
                                <Button
                                    color="primary"
                                    onClick={() => {
                                        setModalOpen(true);
                                        setSelectedRow(null);
                                        setEditID(0);
                                        setCompanies([]);
                                    }}
                                >
                                    Add Contact
                                </Button>
                            </Col>
                        </Row>
                        <CollapseCard title={`${contactTitle} (${dataContacts.length})`} open={true}>
                            <ReactTable
                                data={dataContacts}
                                columns={contactColumns}
                                pageSize={reactTablePageSize}
                                page={reactTablePage}
                                sorted={reactTableSorted}
                                filtered={reactTableFiltered}
                                onPageChange={(pageIndex) => setReactTablePage(pageIndex)}
                                onPageSizeChange={(pageSize, pageIndex) => {
                                    setReactTablePageSize(pageSize);
                                    setReactTablePage(pageIndex);
                                }}
                                onSortedChange={(newSorted) => setReactTableSorted(newSorted)}
                                onFilteredChange={(filtered) => setReactTableFiltered(filtered)}
                                showPagination={true}
                                defaultSorted={[{ id: "Contact Name", desc: false }]}
                                getTrProps={(state, rowInfo) => {
                                    if (!rowInfo) return {};
                                    return {
                                        onClick: (event) => handleRowClick(rowInfo.original, event),
                                        style: { cursor: "pointer" }
                                    };
                                }}
                            />
                        </CollapseCard>
                    </>
                </div>}
            </Container>
        </PermissionWrapper>
    );
}

export default Contacts2;
